import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Box,
  Divider,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ReactGA from "react-ga4";
import { trackEvent } from "../../utils/ga";
import { faqUrl, salesFaqUrl } from "../../data/constant";
import { setSelectedIndex } from "../../services/redux/slices/tabsSlice";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { dateParser } from "../../utils/formatting";
import { RootState } from "../../services/redux/store";
import postsServices from "../../services/api/service/postsService";
import dayjs from "dayjs";
import { decrypt } from "../../utils/crypto";
import {
  authSelector,
  userPermissionSelector,
} from "../../services/redux/slices/authSlice";
import ModalPopup from "../../components/ui/ModalPopup";
import PDFViewer from "../../components/ui/PDFViewer";
import { setProdcuctName } from "../../services/redux/slices/tabsSlice";
import { setStatusPopup } from "../../services/redux/slices/statusPopupSlice"; // Define the types for the props
import LoaderBackdrop from "./LoaderBackdrop";
import { setQuickHelpInfoRoute } from "../../services/redux/slices/tabsSlice";
import { cleardisbursementButtonSlice } from "../../services/redux/slices/disbursementButtonSlice";

interface CustomCardProps {
  title: string;
  icon?: string;
  loanTypeName?: string;
}

const CustomCard: React.FC<CustomCardProps> = ({ title, icon, loanTypeName }) => {
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));
  let url = "";
  if (title === "FAQ") {
    if(loanTypeName?.toLocaleLowerCase() === "elforn") {
      url = salesFaqUrl;
    } else {
      url = faqUrl;
    }
  }
  const [isLoading, setIsLoading] = useState(false);
  const [lanNumber, setLanNumber] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [viewPdfModal, setViewPdfModal] = useState<{
    isOpen: boolean;
    title: string;
  }>({ isOpen: false, title: "" });
  const selectedLoanNumber = useSelector(
    (state: RootState) => state.dashboardData.selectedLoanNumber
  );
  const { userProfile } = useSelector(authSelector);
  const dashboardDataSet = useSelector(
    (state: RootState) => state.dashboardData.sanctionedDisbursedData
  );
  const [certificateInput, setCertificateInput] = useState<{
    certificate: string;
    years: string;
  }>({ certificate: "provsonintstcert", years: "" });
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [pdfUrlAccountStatement, setPdfUrlAccountStatement] =
    useState<string>("");
  const [toDate, setToDate] = useState<Date | null>(null);
  const [minDate, setMinDate] = useState<Date | null>(null);
  const [maxDate, setMaxDate] = useState<Date | null>(null);
  useEffect(() => {
    setLanNumber(selectedLoanNumber);
    const matchedObject = dashboardDataSet.find(
      (data: any) => data.lanNumber === selectedLoanNumber
    );
    const firstDisbDate = new Date(matchedObject?.loanStartDate);
    const maturityDate =
      matchedObject?.statusOfLoan == "M"
        ? new Date(matchedObject?.maturityDate)
        : new Date();
    if (firstDisbDate) {
      let val = new Date(firstDisbDate);
      setFromDate(val);
      setMinDate(firstDisbDate);
    }

    if (maturityDate) {
      setToDate(maturityDate);
      setMaxDate(maturityDate);
    }
  }, [selectedLoanNumber, dashboardDataSet]);

  const viewPdf = async (pdfUrl: string) => {
    setIsLoading(true);

    try {
      const httpsUrl = pdfUrl.replace(/^http:\/\//i, "https://");
      const response = await fetch(httpsUrl);
      if (!response.ok) {
        setIsLoading(false);
        throw new Error(`Failed to fetch PDF: ${response.statusText}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      if (isXsOrSm) {
        setViewPdfModal((prev) => ({ ...prev, isOpen: true }));
        let newWindow: Window | null = window.open("", "_blank");
        if (newWindow) {
          newWindow.location.href = url;
        } else {
          console.error(
            "Failed to open new window. Make sure pop-ups are allowed."
          );
        }

        setIsLoading(false);
      } else {
        setPdfUrlAccountStatement(pdfUrl);
        setViewPdfModal((prev) => ({ ...prev, isOpen: true }));
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Error sending email:", error);
    }
  };
  const accountStatement = async (actionType: string, type: string) => {
    setIsLoading(true);
    if (type === "soa") {
      setViewPdfModal({ isOpen: true, title: "Statement of Account" });
    } else if (type === "schd") {
      setViewPdfModal({ isOpen: true, title: "Repayment Schedule" });
    }
    try {
      const formattedFromDate = fromDate
        ? dayjs(fromDate).format("YYYY-MM-DD")
        : null;
      const formattedToDate = toDate
        ? dayjs(toDate).format("YYYY-MM-DD")
        : null;

      const payload: any = {
        finReference: selectedLoanNumber,
        fromDate: formattedFromDate,
        toDate: formattedToDate,
        type: type,
        cif: decrypt(userProfile?.cifNumber),
        action: actionType,
        loanCat: "term",
      };

      if (type === "cert") {
        payload.fromDate = `${certificateInput.years}-04-01`;
        payload.toDate = `${+certificateInput.years + 1}-03-01`;
        payload.type =
          dayjs().format("YYYY") == certificateInput?.years
            ? "provsonintstcert"
            : "instcert";
        payload.finActiveStatus = "A";
      }

      if (type === "noc") {
        payload.finActiveStatus = "";
      }

      const res = await postsServices.addPost(
        "api/v1/report/accountstatement",
        payload
      );

      setIsLoading(false);
      if (res?.status === 200 && res?.data?.data) {
        if (type === "soa") {
          setViewPdfModal({ isOpen: true, title: "Statement of Account" });
        } else if (type === "schd") {
          setViewPdfModal({ isOpen: true, title: "Repayment Schedule" });
        }
        const pdfUrl = res?.data?.data?.url;

        if (type === "soa") {
          // SOA
          if (actionType === "view") {
            viewPdf(pdfUrl);
            setViewPdfModal({ isOpen: true, title: "Statement of Account" });
          } else if (actionType === "download") {
            downloadPdf(pdfUrl, lanNumber, type);
          } else if (actionType === "email") {
          }
        } else if (type === "schd") {
          // schd
          if (actionType === "view") {
            viewPdf(pdfUrl);
            setViewPdfModal({ isOpen: true, title: "Repayment Schedule" });
          } else if (actionType === "download") {
            downloadPdf(pdfUrl, lanNumber, type);
          } else if (actionType === "email") {
          }
        } else if (type === "noc") {
          // noc
          if (actionType === "view") {
            viewPdf(pdfUrl);
            setViewPdfModal({
              isOpen: true,
              title: "No Objection Certificate",
            });
          } else if (actionType === "download") {
            downloadPdf(pdfUrl, lanNumber, type);
          } else if (actionType === "email") {
          }
        } else {
          if (actionType === "view") {
            viewPdf(pdfUrl);
            setViewPdfModal({ isOpen: true, title: "Interest Certificate" });
          } else if (actionType === "download") {
            downloadPdf(pdfUrl, lanNumber, type);
          } else if (actionType === "email") {
          }
        }
      }
    } catch (error: any) {
      if (type === "soa") {
        setViewPdfModal({ isOpen: false, title: "Statement of Account" });
      } else if (type === "schd") {
        setViewPdfModal({ isOpen: false, title: "Repayment Schedule" });
      }
      const errorMessage = error.response?.data?.message || "An error occurred";
      dispatch(
        setStatusPopup({
          status: "error",
          message: errorMessage,
        })
      );
      setIsLoading(false);
    }
  };
  const handleStatementAction = (action: string, type: string) => {
    if (!type.includes(`wc`)) {
      if (type.includes(`noc`)) {
        const matchedObject = dashboardDataSet?.find(
          (data: any) => data?.lanNumber === selectedLoanNumber
        );
        const maturityYear = +dateParser(matchedObject?.maturityDate, "YY");
        const currentYear = +dateParser("", "YY");
        if (maturityYear > currentYear) {
        } else {
          accountStatement(action, type);
        }
      } else {
        accountStatement(action, type);
      }
    }
  };
  const downloadPdf = async (pdfUrl: string, lan: string, type: string) => {
    try {
      const httpsUrl = pdfUrl.replace(/^http:\/\//i, "https://");
      const response = await fetch(httpsUrl, {
        method: "GET",
        mode: "cors",
        referrerPolicy: "no-referrer",
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch PDF: ${response.statusText} - ${errorText}`
        );
      }

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/pdf")) {
        const errorText = await response.text();
        throw new Error(
          `The fetched file is not a PDF. Response: ${errorText}`
        );
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.target = "_blank";
      anchor.download = `${lan}_${type}.pdf`;

      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      // Handle error state or display error message
    } finally {
      setIsLoading(false);
    }
  };

  const handleAnalytics = (titleVal: String) => {
    if (titleVal == "Loan Account Info") {
      return "Quickhelp_Accountsummary"
    } else if (titleVal == "Account Statements") {
      return "Quickhelp_Statementofaccount"
    } else if (titleVal.trim().toLowerCase() === "amortization schedule".toLowerCase()) {
      return "Quickhelp_Repaymentschedule"
    } else if (titleVal == "Fund request") {
      return "Quickhelp_Request_Disbursement"
    } else if (titleVal == "Payment") {
      return "Quickhelp_Request_Payment"
    } else if (titleVal == "FAQ") {
      return "Quickhelp_FAQ"
    }
  }

  const handleCard = (urlvalue: any, titleVal: String) => {
    const eventName = handleAnalytics(titleVal) || "";
    trackEvent(eventName, decrypt(userProfile?.id), {
      label: eventName,
      category: eventName,
      action: eventName,
    });

    if (titleVal === "Loan Account Info") {
      dispatch(cleardisbursementButtonSlice());
      dispatch(setProdcuctName("TermLoan"));
      dispatch(setQuickHelpInfoRoute("dashboard"));
      navigate("/dashboard/term-loan");
    } else if (titleVal.trim() === "Account Statements") {
      handleStatementAction("view", "soa");
    } else if (titleVal.trim() === "Repayment Schedule") {
      handleStatementAction("view", "schd");
    } else if (titleVal.trim() === "Fund request") {
      const matchVal = dashboardDataSet?.find(
        (data: any) => data?.lanNumber === selectedLoanNumber
      );
      if (
        matchVal?.isExistDisbursementAllowdLoan === false ||
        matchVal?.status != "A"
      ) {
        dispatch(setSelectedIndex(0));
        dispatch(
          setStatusPopup({
            status: "no access",
            // message: `Disbursement requests are not permitted for this loan.`,
            message:
              "Disbursement requests are currently unavailable for this loan",
          })
        );
      } else {
        //dispatch(setProdcuctName("TermLoanDisbursement"));
        dispatch(setSelectedIndex(1));
        //navigate("/dashboard/term-loan");
      }
    } else if (titleVal === "Payment") {
      const matchVal = dashboardDataSet?.find(
        (data: any) => data?.lanNumber === selectedLoanNumber
      );
      if (matchVal?.status != "A") {
        dispatch(setSelectedIndex(0));
        dispatch(
          setStatusPopup({
            status: "no access",
            // message: `Disbursement requests are not permitted for this loan.`,
            message: "Payment requests are currently unavailable for this loan",
          })
        );
      } else {
        dispatch(setSelectedIndex(2));
      }
    } else if (titleVal === "FAQ") {
      window.open(urlvalue, "_blank");
    }
  };
  return (
    <>
      <Card>
        <Box
          sx={{ ".MuiCardContent-root:last-child": { paddingBottom: "12px" } }}
        >
          <CardContent
            className="card-content"
            sx={{
              background: "rgba(255, 255, 255, 0.6)",
              cursor: "pointer",
              padding: "10px 16px 16px 16px",
            }}
            onClick={() => handleCard(url, title)}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start" // Ensures uniform alignment
              sx={{
                minHeight: "50px", // Consistent height for all cards
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "56px", // Fixed size for image container
                  height: "56px",
                  marginRight: "5px", // Consistent spacing
                }}
              >
                <img
                  src={icon}
                  width="56"
                  height="56"
                  alt="icon"
                  style={{ objectFit: "contain" }} // Ensures images maintain aspect ratio
                />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  color: "#a5a3ae",
                  fontSize: "clamp(8px, 1vw, 11px)",
                  fontWeight: "400",
                  lineHeight: "20px",
                  whiteSpace: "wrap", // Prevent text wrapping
                  overflow: "hidden",
                  padding: "0px", // Handles overflow gracefully
                }}
              >
                {title}
              </Typography>
            </Box>
          </CardContent>
        </Box>
      </Card>
      {!isXsOrSm && (
        <ModalPopup
          isOpen={viewPdfModal?.isOpen}
          onClose={() => setViewPdfModal({ ...viewPdfModal, isOpen: false })}
          title={viewPdfModal?.title}
          style={{ minWidth: "80%", height: "100%", overflowX: "hidden" }}
          onNavigate={() => {
            throw new Error("Function not implemented.");
          }}
        >
          {isLoading ? (
            // Skeleton or Loading Indicator
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{ height: "400px" }}
            >
              <CircularProgress size={30} />
            </Box>
          ) : (
            <>
              <Divider sx={{ marginBottom: "10px", marginTop: "10px" }} />
              <PDFViewer pdfUrl={pdfUrlAccountStatement} />
            </>
          )}
        </ModalPopup>
      )}
      <LoaderBackdrop open={isLoading} />
    </>
  );
};
export default CustomCard;
